import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, SlidersHorizontal, Eye, RotateCcw, Loader2 } from "lucide-react";
import { API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import Pagination from "../components/Pagination";
import InfoTip from "../components/InfoTip";
import PrintButton from "../components/PrintButton";
import PrintHeader from "../components/PrintHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROVINCES, VEH_TYPES, LIC_CATEGORIES, PERSON_STATUTS, STATUT_LABELS } from "../lib/constants";
import { toast } from "sonner";

const ADV_DEFAULT = {
  nom: "", prenom: "", telephone: "", date_naissance: "", numero_permis: "", categorie_permis: "",
  immatriculation: "", vin: "", marque: "", modele: "", type_vehicule: "", structure: "",
  province: "", ville: "", statut: "",
};

function DriversTable({ rows }) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-accent/60 text-left">
          <tr>
            {["Nom", "Prenom", "Telephone", "Permis", "Structure", "Immatriculation", "Vehicule", "Statut", "Actions"].map((h) => (
              <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} className="border-t hover:bg-accent/40 transition-colors" data-testid={`driver-row-${i}`}>
              <td className="px-3 py-2.5 font-semibold">{r.nom}</td>
              <td className="px-3 py-2.5">{r.prenom}</td>
              <td className="px-3 py-2.5 mono text-xs">{r.telephone || "-"}</td>
              <td className="px-3 py-2.5 mono text-xs">{r.permis || "-"}</td>
              <td className="px-3 py-2.5 text-xs">{r.structure || "-"}</td>
              <td className="px-3 py-2.5 mono text-xs">{r.immatriculation || "-"}</td>
              <td className="px-3 py-2.5 text-xs">{r.vehicule || "-"}</td>
              <td className="px-3 py-2.5"><StatusBadge value={r.statut} testid={`driver-status-${i}`} /></td>
              <td className="px-3 py-2.5">
                <Button asChild variant="ghost" size="sm" data-testid={`driver-view-${i}`}>
                  <Link to={`/conducteurs/${r.id}`}><Eye className="h-4 w-4 mr-1" /> Consulter</Link>
                </Button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">Aucun resultat.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const { hasPerm } = useAuth();
  const [q, setQ] = useState(params.get("q") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [adv, setAdv] = useState(ADV_DEFAULT);
  const [advResult, setAdvResult] = useState(null);
  const [advPage, setAdvPage] = useState(1);
  const [advPageSize, setAdvPageSize] = useState(25);
  const [advLoading, setAdvLoading] = useState(false);
  const [tab, setTab] = useState("drivers");

  // Selection automatique du premier onglet contenant des resultats
  useEffect(() => {
    if (data) {
      const order = [
        ["drivers", data.drivers.total],
        ["vehicles", data.vehicles.total],
        ["licenses", data.licenses.total],
        ["organizations", data.organizations.total],
      ];
      const found = order.find(([, t]) => t > 0);
      setTab(found ? found[0] : "drivers");
    }
  }, [data]);

  const runSearch = useCallback((query) => {
    if (!query || query.trim().length < 2) return;
    setLoading(true);
    setAdvResult(null);
    API.get("/search", { params: { q: query.trim() } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const initial = params.get("q");
    if (initial) {
      setQ(initial);
      runSearch(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setParams({ q: q.trim() });
    runSearch(q);
  };

  const runAdvanced = (page = 1, size = advPageSize) => {
    setAdvLoading(true);
    const clean = Object.fromEntries(Object.entries(adv).filter(([, v]) => v));
    API.get("/search/advanced", { params: { ...clean, page, page_size: size } })
      .then((r) => { setAdvResult(r.data); setAdvPage(page); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setAdvLoading(false));
  };

  const resetAdv = () => {
    setAdv(ADV_DEFAULT);
    setAdvResult(null);
  };

  return (
    <div className="space-y-6" data-testid="search-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Recherche</h1>
          <p className="text-sm text-muted-foreground">Recherche unifiee dans les bases autorisees du Ministere.</p>
        </div>
        {hasPerm("EXPORT_DATA") && <PrintButton testid="print-results" />}
        <Button variant="outline" onClick={() => setShowAdvanced(!showAdvanced)} data-testid="toggle-advanced">
          <SlidersHorizontal className="h-4 w-4 mr-2" /> Filtres avances
        </Button>
      </div>

      <form onSubmit={submit} className="rounded-xl border-2 border-[#3A75C4]/30 focus-within:border-[#009E60] bg-card p-2 flex items-center gap-2 transition-colors" data-testid="search-form">
        <Search className="ml-2 h-5 w-5 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, permis, immatriculation, VIN, telephone, structure..."
          className="w-full bg-transparent px-2 py-2.5 focus:outline-none" data-testid="search-input" />
        <Button type="submit" disabled={loading} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="search-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rechercher"}
        </Button>
      </form>

      {showAdvanced && (
        <Card data-testid="advanced-filters">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              Filtres avances
              <InfoTip testid="tip-advanced" text="Combinez plusieurs criteres pour affiner la recherche. La recherche ignore la casse, les accents, les espaces et les tirets." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Input placeholder="Nom" value={adv.nom} onChange={(e) => setAdv({ ...adv, nom: e.target.value })} data-testid="adv-nom" />
              <Input placeholder="Prenom" value={adv.prenom} onChange={(e) => setAdv({ ...adv, prenom: e.target.value })} data-testid="adv-prenom" />
              <Input placeholder="Telephone" value={adv.telephone} onChange={(e) => setAdv({ ...adv, telephone: e.target.value })} data-testid="adv-telephone" />
              <Input type="date" value={adv.date_naissance} onChange={(e) => setAdv({ ...adv, date_naissance: e.target.value })} data-testid="adv-dob" />
              <Input placeholder="Numero de permis" value={adv.numero_permis} onChange={(e) => setAdv({ ...adv, numero_permis: e.target.value })} data-testid="adv-permis" />
              <Select value={adv.categorie_permis || "all"} onValueChange={(v) => setAdv({ ...adv, categorie_permis: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="adv-categorie"><SelectValue placeholder="Categorie permis" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Categorie (toutes)</SelectItem>
                  {LIC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Immatriculation" value={adv.immatriculation} onChange={(e) => setAdv({ ...adv, immatriculation: e.target.value })} data-testid="adv-immat" />
              <div className="relative">
                <Input placeholder="VIN / Chassis" value={adv.vin} onChange={(e) => setAdv({ ...adv, vin: e.target.value })} className="pr-8" data-testid="adv-vin" />
                <span className="absolute right-2 top-1/2 -translate-y-1/2"><InfoTip testid="tip-vin" text="Numero d'identification unique du vehicule (17 caracteres)." /></span>
              </div>
              <Input placeholder="Marque" value={adv.marque} onChange={(e) => setAdv({ ...adv, marque: e.target.value })} data-testid="adv-marque" />
              <Input placeholder="Modele" value={adv.modele} onChange={(e) => setAdv({ ...adv, modele: e.target.value })} data-testid="adv-modele" />
              <Select value={adv.type_vehicule || "all"} onValueChange={(v) => setAdv({ ...adv, type_vehicule: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="adv-type"><SelectValue placeholder="Type vehicule" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Type (tous)</SelectItem>
                  {VEH_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Structure / Entreprise" value={adv.structure} onChange={(e) => setAdv({ ...adv, structure: e.target.value })} data-testid="adv-structure" />
              <Select value={adv.province || "all"} onValueChange={(v) => setAdv({ ...adv, province: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="adv-province"><SelectValue placeholder="Province" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Province (toutes)</SelectItem>
                  {PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Ville" value={adv.ville} onChange={(e) => setAdv({ ...adv, ville: e.target.value })} data-testid="adv-ville" />
              <Select value={adv.statut || "all"} onValueChange={(v) => setAdv({ ...adv, statut: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="adv-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Statut (tous)</SelectItem>
                  {PERSON_STATUTS.map((s) => <SelectItem key={s} value={s}>{STATUT_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-4 flex gap-3">
              <Button variant="outline" onClick={resetAdv} data-testid="adv-reset"><RotateCcw className="h-4 w-4 mr-2" /> Reinitialiser</Button>
              <Button onClick={() => runAdvanced(1)} disabled={advLoading} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="adv-search">
                {advLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />} Rechercher
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {(data || advResult) && (
        <PrintHeader title="Resultats de recherche"
          subtitle={advResult ? "Recherche avancee" : `Recherche : "${data?.query || ""}"`} />
      )}

      {advResult && (
        <Card data-testid="advanced-results">
          <CardHeader><CardTitle className="text-base font-heading">{advResult.total.toLocaleString("fr-FR")} resultat(s) — recherche avancee</CardTitle></CardHeader>
          <CardContent>
            <DriversTable rows={advResult.items} />
            <Pagination total={advResult.total} page={advPage} pageSize={advPageSize}
              onPage={(p) => runAdvanced(p)} onPageSize={(s) => { setAdvPageSize(s); runAdvanced(1, s); }} testid="adv-pagination" />
          </CardContent>
        </Card>
      )}

      {!advResult && loading && (
        <div className="space-y-3"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
      )}

      {!advResult && data && !loading && (data.drivers.total + data.vehicles.total + data.licenses.total + data.organizations.total === 0) && (
        <div className="rounded-xl border border-dashed p-10 text-center" data-testid="search-no-results">
          <Search className="mx-auto h-10 w-10 mb-3 opacity-40 text-muted-foreground" />
          <p className="text-sm font-medium">Aucun resultat pour « {data.query} » dans les bases ALARA.</p>
          <p className="mt-1 text-xs text-muted-foreground">Verifiez l'orthographe ou essayez les filtres avances. La recherche ignore la casse, les accents, les espaces et les tirets.</p>
        </div>
      )}

      {!advResult && data && !loading && (data.drivers.total + data.vehicles.total + data.licenses.total + data.organizations.total > 0) && (
        <Tabs value={tab} onValueChange={setTab} data-testid="search-results">
          <TabsList className="flex-wrap">
            <TabsTrigger value="drivers" data-testid="tab-drivers">Conducteurs ({data.drivers.total})</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="tab-vehicles">Vehicules ({data.vehicles.total})</TabsTrigger>
            <TabsTrigger value="licenses" data-testid="tab-licenses">Permis ({data.licenses.total})</TabsTrigger>
            <TabsTrigger value="organizations" data-testid="tab-organizations">Structures ({data.organizations.total})</TabsTrigger>
          </TabsList>

          <TabsContent value="drivers">
            {data.drivers.total > 5 && (
              <p className="mb-2 text-xs text-muted-foreground">
                5 premiers resultats affiches — <Link className="text-[#3A75C4] underline" to={`/conducteurs?q=${encodeURIComponent(data.query)}`}>voir tout dans Conducteurs</Link>
              </p>
            )}
            <DriversTable rows={data.drivers.items} />
          </TabsContent>

          <TabsContent value="vehicles">
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-accent/60 text-left">
                  <tr>{["Immatriculation", "VIN", "Marque", "Modele", "Type", "Statut", "Actions"].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {data.vehicles.items.map((v, i) => (
                    <tr key={v.id} className="border-t hover:bg-accent/40" data-testid={`vehicle-row-${i}`}>
                      <td className="px-3 py-2.5 mono text-xs font-semibold">{v.immatriculation}</td>
                      <td className="px-3 py-2.5 mono text-xs">{v.vin || "-"}</td>
                      <td className="px-3 py-2.5">{v.marque}</td>
                      <td className="px-3 py-2.5">{v.modele}</td>
                      <td className="px-3 py-2.5 text-xs">{v.type}</td>
                      <td className="px-3 py-2.5"><StatusBadge value={v.statut} /></td>
                      <td className="px-3 py-2.5">
                        <Button asChild variant="ghost" size="sm" data-testid={`vehicle-view-${i}`}>
                          <Link to={`/vehicules/${v.id}`}><Eye className="h-4 w-4 mr-1" /> Consulter</Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {data.vehicles.items.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Aucun vehicule trouve.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="licenses">
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-accent/60 text-left">
                  <tr>{["Numero", "Categorie", "Delivrance", "Expiration", "Statut"].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {data.licenses.items.map((l, i) => (
                    <tr key={l.id} className="border-t hover:bg-accent/40" data-testid={`license-row-${i}`}>
                      <td className="px-3 py-2.5 mono text-xs font-semibold">{l.numero}</td>
                      <td className="px-3 py-2.5">{l.categorie}</td>
                      <td className="px-3 py-2.5 text-xs">{l.date_delivrance || "-"}</td>
                      <td className="px-3 py-2.5 text-xs">{l.date_expiration || "-"}</td>
                      <td className="px-3 py-2.5"><StatusBadge value={l.statut} /></td>
                    </tr>
                  ))}
                  {data.licenses.items.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Aucun permis trouve.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="organizations">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.organizations.items.map((o, i) => (
                <Card key={o.id} data-testid={`org-card-${i}`}>
                  <CardContent className="p-4">
                    <p className="font-heading font-semibold">{o.nom}</p>
                    <p className="text-xs text-muted-foreground">{o.sigle} — {o.type}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{o.ville}{o.province ? `, ${o.province}` : ""}</p>
                  </CardContent>
                </Card>
              ))}
              {data.organizations.items.length === 0 && <p className="text-sm text-muted-foreground">Aucune structure trouvee.</p>}
            </div>
          </TabsContent>
        </Tabs>
      )}

      {!data && !loading && !advResult && (
        <div className="rounded-xl border border-dashed p-10 text-center text-muted-foreground" data-testid="search-empty">
          <Search className="mx-auto h-10 w-10 mb-3 opacity-40" />
          <p className="text-sm">Saisissez au moins 2 caracteres pour lancer une recherche.</p>
          <p className="mt-1 text-xs">La recherche ignore la casse, les accents, les espaces et les tirets (ex : GA 123 AA = GA-123-AA = ga123aa).</p>
        </div>
      )}
    </div>
  );
}
